import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";

export default function CourseList() {
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState({ chapters: [] });

  const load = () => Promise.all([api.get("/course"), api.get("/progress")]).then(([c, p]) => {
    setChapters(c.data);
    setProgress(p.data);
  });

  useEffect(() => { load(); }, []);

  const reset = async (e, chapter_id) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.delete(`/progress/${chapter_id}`);
      toast.success(`Chapter ${chapter_id} reset`);
      load();
    } catch (err) { toast.error("Reset failed"); }
  };

  const map = new Map(progress.chapters.map(c => [c.chapter_id, c]));

  const byCat = chapters.reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3">The full curriculum</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-10">13 chapters, one lifetime skill.</h1>

        {Object.entries(byCat).map(([cat, list]) => (
          <section key={cat} className="mb-10">
            <div className="label-caps mb-4">{cat}</div>
            <div className="space-y-3">
              {list.map(ch => {
                const p = map.get(ch.id);
                const locked = ch.locked;
                return (
                  <Link key={ch.id} to={`/course/${ch.id}`} data-testid={`course-item-${ch.id}`}>
                    <Card className={`p-6 flex items-center gap-6 transition-colors ${locked ? "opacity-70 hover:border-accent" : "hover:border-primary"}`}>
                      <div className="font-serif text-4xl text-muted-foreground w-12">{String(ch.id).padStart(2, "0")}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-2xl leading-tight flex items-center gap-2">
                          {ch.title}
                          {locked && <Lock size={14} className="text-accent" data-testid={`locked-icon-${ch.id}`} />}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{ch.summary}</p>
                      </div>
                      <div className="hidden sm:flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">{ch.reading_time} min</div>
                          {locked ? <div className="text-[10px] label-caps mt-0.5 text-accent">Enroll to unlock</div> :
                            p && <div className="text-xs mt-0.5">Score: <span className="font-mono">{p.score}%</span></div>}
                        </div>
                        {locked ? (
                          <Lock size={20} className="text-accent" />
                        ) : p ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => reset(e, ch.id)}
                            title="Reset chapter"
                            data-testid={`reset-chapter-${ch.id}`}
                          >
                            <RotateCcw size={14} />
                          </Button>
                        ) : (
                          <Circle size={20} className="text-muted-foreground" />
                        )}
                        {!locked && p?.passed && <CheckCircle2 size={20} className="text-primary" />}
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </Layout>
  );
}
